import { needsAutoClassChangeMigration } from "utils/autoClassChangeMigration";
import { isNewUserOnboardingPending } from "utils/firstRunOnboarding";
import { buildLegacyScheduleConversionDraft } from "utils/legacyScheduleConversionWizard";

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_VERSION = "v4";
export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_VIDEO_URL = "https://www.youtube.com/watch?v=0T55S2mX8Fk";
export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_VIDEO_EMBED_URL = "https://www.youtube.com/embed/0T55S2mX8Fk";

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS = {
  NEW: "new",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  NOT_APPLICABLE: "not_applicable",
};

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_STEPS = {
  WELCOME: "welcome",
  AUTO_CLASS_CHANGE: "autoClassChange",
  SCHEDULE_UPGRADE: "scheduleUpgrade",
  LEGACY_AUTOMATIONS: "legacyAutomations",
  DESTINATION: "destination",
  FEEDBACK: "feedback",
  COMPLETE: "complete",
};

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_DESTINATIONS = {
  TEACHER_HUB: "teacher_hub",
  SCHEDULE_MANAGER: "schedule_manager",
};

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_FEEDBACK_OUTCOMES = {
  READY_TO_GO: "ready_to_go",
  CLASS_TIMING_WRONG: "class_timing_wrong",
  SCHEDULE_MISSING_OR_INCORRECT: "schedule_missing_or_incorrect",
  /** @deprecated Kept for historical responses collected before the July 2026 feedback revision. */
  SCHEDULE_NEEDS_WORK: "schedule_needs_work",
  CONFUSING_OR_SKIPPED: "confusing_or_skipped",
  SOMETHING_BROKE: "something_broke",
};

export const AUTO_CLASS_CHANGE_MIGRATION_STATUS = {
  PENDING: "pending",
  APPLIED: "applied",
  SKIPPED: "skipped",
  NOT_NEEDED: "not_needed",
};

export const SCHEDULE_UPGRADE_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  SKIPPED: "skipped",
};

const normalizeUpgradeDateValue = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsedValue = Date.parse(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") {
    const nanoseconds = typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanoseconds / 1000000);
  }
  return null;
};

export const createDefaultLegacyScheduleJuly2026ConversionState = () => ({
  version: LEGACY_SCHEDULE_JULY_2026_CONVERSION_VERSION,
  status: LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.NEW,
  currentStepId: LEGACY_SCHEDULE_JULY_2026_CONVERSION_STEPS.WELCOME,
  welcomeVideoSkippedAt: null,
  autoClassChange: {
    status: AUTO_CLASS_CHANGE_MIGRATION_STATUS.PENDING,
    choice: null,
    shiftMinutes: null,
    offsetMinutesBeforeStart: null,
    appliedAt: null,
  },
  scheduleUpgrade: {
    status: SCHEDULE_UPGRADE_STATUS.PENDING,
    path: null,
    completedAt: null,
  },
  postUpgradeDestination: null,
  feedback: {
    outcome: null,
    comments: "",
    attentionAreas: [],
    submittedAt: null,
  },
  feedbackCompletedAt: null,
  feedbackSkippedAt: null,
  startedAt: null,
  completedAt: null,
});

export const createNotApplicableLegacyScheduleJuly2026ConversionState = () => ({
  ...createDefaultLegacyScheduleJuly2026ConversionState(),
  status: LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.NOT_APPLICABLE,
  currentStepId: LEGACY_SCHEDULE_JULY_2026_CONVERSION_STEPS.COMPLETE,
  completedAt: Date.now(),
});

export const normalizeLegacyScheduleJuly2026ConversionState = (upgradeState) => {
  const defaults = createDefaultLegacyScheduleJuly2026ConversionState();
  if (!upgradeState || typeof upgradeState !== "object") {
    return defaults;
  }

  return {
    ...defaults,
    ...upgradeState,
    version: upgradeState.version || defaults.version,
    status: upgradeState.status || defaults.status,
    currentStepId: upgradeState.currentStepId || defaults.currentStepId,
    welcomeVideoSkippedAt: normalizeUpgradeDateValue(upgradeState.welcomeVideoSkippedAt),
    feedbackCompletedAt: normalizeUpgradeDateValue(upgradeState.feedbackCompletedAt),
    feedbackSkippedAt: normalizeUpgradeDateValue(upgradeState.feedbackSkippedAt),
    startedAt: normalizeUpgradeDateValue(upgradeState.startedAt),
    completedAt: normalizeUpgradeDateValue(upgradeState.completedAt),
    autoClassChange: {
      ...defaults.autoClassChange,
      ...(upgradeState.autoClassChange || {}),
      appliedAt: normalizeUpgradeDateValue(upgradeState.autoClassChange?.appliedAt),
    },
    scheduleUpgrade: {
      ...defaults.scheduleUpgrade,
      ...(upgradeState.scheduleUpgrade || {}),
      completedAt: normalizeUpgradeDateValue(upgradeState.scheduleUpgrade?.completedAt),
    },
    feedback: {
      ...defaults.feedback,
      ...(upgradeState.feedback || {}),
      outcome: upgradeState.feedback?.outcome || null,
      comments: typeof upgradeState.feedback?.comments === "string"
        ? upgradeState.feedback.comments
        : "",
      attentionAreas: Array.isArray(upgradeState.feedback?.attentionAreas)
        ? upgradeState.feedback.attentionAreas.filter((area) => typeof area === "string" && area.trim())
        : [],
      submittedAt: normalizeUpgradeDateValue(upgradeState.feedback?.submittedAt),
    },
  };
};

export const toLegacyScheduleJuly2026ConversionFirestoreState = (upgradeState) => ({
  ...upgradeState,
  welcomeVideoSkippedAt: upgradeState?.welcomeVideoSkippedAt ? new Date(upgradeState.welcomeVideoSkippedAt) : null,
  feedbackCompletedAt: upgradeState?.feedbackCompletedAt ? new Date(upgradeState.feedbackCompletedAt) : null,
  feedbackSkippedAt: upgradeState?.feedbackSkippedAt ? new Date(upgradeState.feedbackSkippedAt) : null,
  startedAt: upgradeState?.startedAt ? new Date(upgradeState.startedAt) : null,
  completedAt: upgradeState?.completedAt ? new Date(upgradeState.completedAt) : null,
  autoClassChange: {
    ...(upgradeState?.autoClassChange || {}),
    appliedAt: upgradeState?.autoClassChange?.appliedAt ? new Date(upgradeState.autoClassChange.appliedAt) : null,
  },
  scheduleUpgrade: {
    ...(upgradeState?.scheduleUpgrade || {}),
    completedAt: upgradeState?.scheduleUpgrade?.completedAt ? new Date(upgradeState.scheduleUpgrade.completedAt) : null,
  },
  feedback: {
    ...(upgradeState?.feedback || {}),
    submittedAt: upgradeState?.feedback?.submittedAt ? new Date(upgradeState.feedback.submittedAt) : null,
  },
});

export const isLegacyScheduleJuly2026ConversionComplete = (upgradeState) =>
  normalizeLegacyScheduleJuly2026ConversionState(upgradeState).status === LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.COMPLETED;

export const isLegacyScheduleJuly2026ConversionNotApplicable = (upgradeState) =>
  normalizeLegacyScheduleJuly2026ConversionState(upgradeState).status === LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.NOT_APPLICABLE;

export const isLegacyScheduleJuly2026ConversionWizardEligible = (user) => {
  if (user?.provisioningIncomplete === true || isNewUserOnboardingPending(user)) {
    return false;
  }

  const status = normalizeLegacyScheduleJuly2026ConversionState(
    user?.legacyScheduleJuly2026Conversion
  ).status;

  return ![
    LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.COMPLETED,
    LEGACY_SCHEDULE_JULY_2026_CONVERSION_STATUS.NOT_APPLICABLE,
  ].includes(status);
};

export const hasMeaningfulScheduleCalendarSetup = (scheduleCalendar = {}) => {
  const templates = Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [];
  const assignments = Array.isArray(scheduleCalendar?.assignments) ? scheduleCalendar.assignments : [];
  const hasClassBlocks = templates.some((template) =>
    template?.active !== false &&
    Array.isArray(template?.classes) &&
    template.classes.some((scheduleClass) => scheduleClass?.classId && scheduleClass?.startTime && scheduleClass?.endTime)
  );
  const hasActiveAssignments = assignments.some((assignment) => assignment?.active !== false && assignment?.templateId);

  return hasClassBlocks && hasActiveAssignments;
};

export const shouldShowSkippedScheduleSetupReminder = (user, scheduleCalendar = user?.settings?.scheduleCalendar) => {
  const conversionState = normalizeLegacyScheduleJuly2026ConversionState(user?.legacyScheduleJuly2026Conversion);
  const scheduleUpgrade = conversionState.scheduleUpgrade || {};
  const skippedOrManual =
    scheduleUpgrade.status === SCHEDULE_UPGRADE_STATUS.SKIPPED ||
    scheduleUpgrade.path === "manual";

  return skippedOrManual && !hasMeaningfulScheduleCalendarSetup(scheduleCalendar);
};

export const canOfferLegacyScheduleConversionInScheduleManager = ({
  user,
  classes = [],
  scheduleCalendar = user?.settings?.scheduleCalendar,
} = {}) => {
  if (!shouldShowSkippedScheduleSetupReminder(user, scheduleCalendar)) return false;

  return buildLegacyScheduleConversionDraft({
    userSettings: user?.settings || {},
    classes,
  }).hasUsablePatterns;
};

export const needsLegacyScheduleJuly2026ConversionSteps = ({
  classes = [],
  userSettings = {},
} = {}) => {
  if (needsAutoClassChangeMigration(classes)) return true;
  return buildLegacyScheduleConversionDraft({ userSettings, classes }).hasUsablePatterns;
};

export const LEGACY_SCHEDULE_JULY_2026_CONVERSION_SUPPRESSED_PATHS = [
  "/whats-new",
  "/admin/legacy-schedule-upgrade-preview",
  "/admin/schedule-manager-setup-preview",
];

export const shouldShowLegacyScheduleJuly2026ConversionWizard = ({
  user,
  classes = [],
  classRoutesReady = false,
  pathname = "",
  scheduleCalendarLoadStatus = "idle",
} = {}) => {
  const normalizedPathname = String(pathname || "");

  if (!user?.uid || !classRoutesReady) return false;
  if (normalizedPathname.includes("/share/")) return false;
  if (LEGACY_SCHEDULE_JULY_2026_CONVERSION_SUPPRESSED_PATHS.includes(normalizedPathname)) return false;
  if (!["loaded", "error"].includes(scheduleCalendarLoadStatus)) return false;
  return isLegacyScheduleJuly2026ConversionWizardEligible(user);
};
